export const INTEGRATED_RESOURCE_PLATFORMS = [
    'slack',
    'jira',
    'github',
    'agent',
];
export const RESERVED_RESOURCE_PLATFORMS = [
    'gmail',
    'aws',
    'moesif',
];
function safeIsoFromSlackTs(latestTs, observedAt) {
    if (!latestTs)
        return observedAt;
    const num = Number.parseFloat(latestTs);
    if (!Number.isFinite(num) || num < 0)
        return observedAt;
    try {
        const d = new Date(num * 1000);
        return Number.isNaN(d.getTime()) ? observedAt : d.toISOString();
    }
    catch {
        return observedAt;
    }
}
function canonicalSegment(value, label) {
    const normalized = value.trim();
    if (!normalized)
        throw new Error(`${label} cannot be empty`);
    if (normalized.includes(':'))
        throw new Error(`${label} cannot contain ':'`);
    return normalized;
}
/** Stable id for a Slack conversation, independent of related cards. */
export function canonicalSlackResourceId(workspaceId, channelId, rootTs) {
    return `slack:${canonicalSegment(workspaceId, 'workspaceId')}:${canonicalSegment(channelId, 'channelId')}:${canonicalSegment(rootTs, 'rootTs')}`;
}
/** Pure projection of an existing Slack attention record. */
export function slackResourceFromAttentionRecord(workspaceId, record, observedAt) {
    const rootTs = record.rootTs ?? record.messageTs;
    const latestActivityAt = safeIsoFromSlackTs(record.latestTs, observedAt);
    return {
        id: canonicalSlackResourceId(workspaceId, record.channelId, rootTs),
        platform: 'slack',
        title: record.channelName || 'Slack Conversation',
        summary: record.summary,
        url: record.permalink,
        sourceState: { platform: 'slack', status: 'unknown' },
        sourceUpdatedAt: latestActivityAt,
        observedAt,
        workspaceId,
        channelId: record.channelId,
        channelName: record.channelName,
        channelType: record.channelType,
        rootTs,
        latestActivityAt,
    };
}
export function canonicalJiraResourceId(issueKey) {
    return `jira:${canonicalSegment(issueKey, 'issueKey')}`;
}
export function canonicalGitHubResourceId(owner, repository, number) {
    if (!Number.isInteger(number) || number < 1)
        throw new Error('number must be a positive integer');
    return `github:${canonicalSegment(owner, 'owner')}/${canonicalSegment(repository, 'repository')}#${number}`;
}
export function canonicalAgentResourceId(collector, sessionId) {
    return `agent:${canonicalSegment(collector, 'collector')}:${canonicalSegment(sessionId, 'sessionId')}`;
}
export function canonicalAttentionId(primaryResourceId, reasonKey) {
    const reason = canonicalSegment(reasonKey, 'reasonKey');
    return `attention:${encodeURIComponent(primaryResourceId)}:${encodeURIComponent(reason)}`;
}
export function canonicalResourceRelationId(fromResourceId, toResourceId, type) {
    return `relation:${encodeURIComponent(fromResourceId)}:${encodeURIComponent(toResourceId)}:${type}`;
}
function resourceRelationKey(relation) {
    return `${relation.fromResourceId}\u0000${relation.toResourceId}\u0000${relation.type}`;
}
/** Builds a bidirectional index without mutating the input array. */
export function buildRelationIndex(relations) {
    const byResource = new Map();
    for (const relation of relations) {
        for (const resourceId of [relation.fromResourceId, relation.toResourceId]) {
            const current = byResource.get(resourceId) ?? [];
            current.push(relation);
            byResource.set(resourceId, current);
        }
    }
    return byResource;
}
function otherResourceId(relation, resourceId) {
    if (relation.fromResourceId === resourceId)
        return relation.toResourceId;
    return relation.fromResourceId;
}
/** Returns neighboring resources, de-duplicated and filtered by relation status. */
export function relatedResourceIds(resourceId, index, status = 'confirmed') {
    const related = new Set();
    const relations = index.get(resourceId) ?? [];
    for (const relation of relations) {
        if (relation.status === status) {
            related.add(otherResourceId(relation, resourceId));
        }
    }
    return [...related];
}
function mergeRelationStatus(existing, candidate) {
    // Explicit attach/detach writes win; suggested never demotes.
    if (candidate === 'rejected')
        return 'rejected';
    if (candidate === 'confirmed')
        return 'confirmed';
    if (existing === 'confirmed')
        return 'confirmed';
    if (existing === 'rejected')
        return 'rejected';
    return 'suggested';
}
/** Upsert relation: preserves original creation timestamp and merges evidence. */
export function upsertResourceRelation(relations, candidate) {
    const candidateKey = resourceRelationKey(candidate);
    const existingIndex = relations.findIndex((relation) => resourceRelationKey(relation) === candidateKey);
    if (existingIndex < 0)
        return [...relations, candidate];
    const existing = relations[existingIndex];
    const evidence = new Map(existing.evidence.map((item) => [item.id, item]));
    for (const item of candidate.evidence)
        evidence.set(item.id, item);
    const merged = {
        ...existing,
        ...candidate,
        id: existing.id || candidate.id,
        status: mergeRelationStatus(existing.status, candidate.status),
        createdAt: existing.createdAt,
        evidence: [...evidence.values()],
    };
    return relations.map((relation, index) => index === existingIndex ? merged : relation);
}
/** Platform prefix of a canonical resource id (`jira:DEV-1` → `jira`). */
export function resourcePlatformFromId(resourceId) {
    const value = String(resourceId);
    const colon = value.indexOf(':');
    if (colon <= 0)
        return '';
    return value.slice(0, colon);
}
export function isIntegratedResourcePlatform(platform) {
    return INTEGRATED_RESOURCE_PLATFORMS.includes(platform);
}
export function isReservedResourcePlatform(platform) {
    return RESERVED_RESOURCE_PLATFORMS.includes(platform);
}
/** Provisional card identity used by the SPA until a live Resource is materialized. */
export function provisionalCardResourceId(platform, cardId) {
    return `card:${canonicalSegment(platform, 'platform')}:${canonicalSegment(cardId, 'cardId')}`;
}
/** Stable id for a user-created Radar work-context hub (no Jira issue required). */
export function canonicalManualHubResourceId(hubId) {
    return `manual:${canonicalSegment(hubId, 'hubId')}`;
}
/** Build a confirmed manual edge between two resources. */
export function manualResourceRelation(fromResourceId, toResourceId, observedAt, evidenceId, status = 'confirmed') {
    return {
        id: canonicalResourceRelationId(fromResourceId, toResourceId, 'related'),
        fromResourceId,
        toResourceId,
        type: 'related',
        status,
        evidence: [
            {
                id: evidenceId,
                kind: 'manual',
                sourcePlatform: evidencePlatformFromResourceId(fromResourceId),
                sourceResourceId: fromResourceId,
                observedAt,
            },
        ],
        createdAt: observedAt,
        lastVerifiedAt: observedAt,
    };
}
export function canonicalWorkContextId(sortedMemberIds) {
    if (sortedMemberIds.length === 0) {
        throw new Error('WorkContext requires at least one member');
    }
    return `workctx:${sortedMemberIds.map((id) => encodeURIComponent(String(id))).join('|')}`;
}
function pickWorkContextAnchor(members) {
    const jiraMembers = members.filter((id) => resourcePlatformFromId(id) === 'jira');
    if (jiraMembers.length > 0) {
        return [...jiraMembers].sort((a, b) => String(a).localeCompare(String(b)))[0];
    }
    const manualMembers = members.filter((id) => resourcePlatformFromId(id) === 'manual');
    if (manualMembers.length > 0) {
        return [...manualMembers].sort((a, b) => String(a).localeCompare(String(b)))[0];
    }
    return [...members].sort((a, b) => String(a).localeCompare(String(b)))[0];
}
function unionFindParent(parent, id) {
    const current = parent.get(id) ?? id;
    if (current === id)
        return id;
    const root = unionFindParent(parent, current);
    parent.set(id, root);
    return root;
}
function unionFindMerge(parent, left, right) {
    const rootLeft = unionFindParent(parent, left);
    const rootRight = unionFindParent(parent, right);
    if (rootLeft === rootRight)
        return;
    if (rootLeft < rootRight)
        parent.set(rootRight, rootLeft);
    else
        parent.set(rootLeft, rootRight);
}
/**
 * Clusters resources into WorkContexts via connected components of relations.
 * Isolated seed ids become singleton contexts. Relation endpoints outside the
 * seed set are pulled in when they connect to a seed (e.g. jira:KEY hubs).
 */
export function clusterResourcesByRelations(seedResourceIds, relations, status = 'confirmed') {
    const parent = new Map();
    const ensure = (id) => {
        if (!parent.has(id))
            parent.set(id, id);
    };
    for (const id of seedResourceIds)
        ensure(String(id));
    for (const relation of relations) {
        if (relation.status !== status)
            continue;
        const from = String(relation.fromResourceId);
        const to = String(relation.toResourceId);
        ensure(from);
        ensure(to);
        unionFindMerge(parent, from, to);
    }
    const seedSet = new Set(seedResourceIds.map((id) => String(id)));
    const buckets = new Map();
    for (const id of parent.keys()) {
        const root = unionFindParent(parent, id);
        const bucket = buckets.get(root) ?? [];
        bucket.push(id);
        buckets.set(root, bucket);
    }
    const contexts = [];
    for (const members of buckets.values()) {
        if (!members.some((id) => seedSet.has(id)))
            continue;
        const memberResourceIds = members
            .map((id) => id)
            .sort((a, b) => String(a).localeCompare(String(b)));
        contexts.push({
            id: canonicalWorkContextId(memberResourceIds),
            anchorResourceId: pickWorkContextAnchor(memberResourceIds),
            memberResourceIds,
        });
    }
    return contexts.sort((a, b) => String(a.anchorResourceId).localeCompare(String(b.anchorResourceId)));
}
function evidencePlatformFromResourceId(resourceId) {
    const platform = resourcePlatformFromId(resourceId);
    if (isIntegratedResourcePlatform(platform) || isReservedResourcePlatform(platform)) {
        return platform;
    }
    if (platform === 'card') {
        const nested = String(resourceId).slice('card:'.length).split(':')[0] ?? '';
        if (isIntegratedResourcePlatform(nested) || isReservedResourcePlatform(nested))
            return nested;
        if (nested === 'agents')
            return 'agent';
    }
    // Manual hubs and tool cards are not ResourcePlatforms; evidence still needs a value.
    return 'jira';
}
/** Build a typed relation toward a Jira issue key with explicit-key evidence. */
export function relationToJiraIssue(fromResourceId, issueKey, type, observedAt, evidenceId, status = 'confirmed') {
    const toResourceId = canonicalJiraResourceId(issueKey);
    return {
        id: canonicalResourceRelationId(fromResourceId, toResourceId, type),
        fromResourceId,
        toResourceId,
        type,
        status,
        evidence: [
            {
                id: evidenceId,
                kind: 'explicit-key',
                sourcePlatform: evidencePlatformFromResourceId(fromResourceId),
                sourceResourceId: fromResourceId,
                locator: issueKey,
                observedAt,
            },
        ],
        createdAt: observedAt,
        lastVerifiedAt: observedAt,
    };
}
//# sourceMappingURL=workContext.js.map